import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Functions
export const apiClient = {
  // Tests
  getTests: () => api.get('/tests'),
  getTest: (slug) => api.get(`/tests/${slug}`),
  
  // Scores
  saveScore: (scoreData) => api.post('/scores', scoreData),
  getScoreHistory: () => api.get('/scores/history'),
  getProgress: () => api.get('/progress'),
  getPercentiles: () => api.get('/percentiles'),
  getLeaderboard: (testSlug) => api.get(`/leaderboard/${testSlug}`),
  
  // Credits
  useFreeCredit: (testSlug) => api.post('/credits/use', { test_slug: testSlug }),
  checkCreditAccess: (testSlug) => api.get(`/credits/check/${testSlug}`),
  
  // Auth - Force Password Change (for TDF provisioned users)
  forcePasswordChange: (newPassword) => api.post('/auth/force-password-change', { new_password: newPassword }),
  
  // Payments
  createCheckout: (originUrl, planType = 'premium', testSlug = null) => api.post('/payments/checkout', { origin_url: originUrl, plan_type: planType, test_slug: testSlug }),
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  
  // Guide
  createGuideCheckout: (originUrl, useDiscountOffer = false) => api.post('/guide/checkout', {
    origin_url: originUrl,
    use_discount_offer: useDiscountOffer,
  }),
  getGuideStatus: (sessionId) => api.get(`/guide/status/${sessionId}`),
  checkGuideOwnership: () => api.get('/guide/check'),
  downloadGuide: () => api.get('/guide/download'),
  getGuideOfferStatus: () => api.get('/guide/offer/status'),
  dismissGuideOffer: () => api.post('/guide/offer/dismiss'),
  
  // Sale
  getSaleStatus: () => api.get('/sale/status'),
  
  // Admin
  getAdminUsers: () => api.get('/admin/users'),
  searchAdminUsers: (params) => api.get('/admin/users/search', { params }),
  updateAdminUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  getAdminStats: () => api.get('/admin/stats'),
  getSubscriptionHealth: () => api.get('/admin/subscription-health'),
  restoreUserSubscription: (userId) => api.post(`/admin/users/${userId}/restore-subscription`),
  getAdminContent: () => api.get('/admin/content'),
  updateAdminContent: (section, content) => api.put('/admin/content', { section, content }),
  grantUserMembership: (userId, days = 30) => api.post(`/admin/users/${userId}/grant-membership?days=${days}`),
  grantUserGuide: (userId) => api.post(`/admin/users/${userId}/grant-guide`),
  revokeUserMembership: (userId) => api.post(`/admin/users/${userId}/revoke-membership`),
  getRecentActivity: (limit = 20) => api.get(`/admin/recent-activity?limit=${limit}`),
  createUser: (userData) => api.post('/admin/users/create', userData),
  setUserCredits: (userId, freeCredits) => api.post(`/admin/users/${userId}/set-credits`, { free_credits: freeCredits }),
  addUserCredits: (userId, amount = 1) => api.post(`/admin/users/${userId}/add-credits?amount=${amount}`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  adminResetPassword: (userId, sendEmail = true) => api.post(`/admin/users/${userId}/reset-password?send_email=${sendEmail}`),

  // Test maintenance (admin)
  getTestMaintenance: () => api.get('/admin/tests/maintenance'),
  setTestMaintenance: (slug, enabled, message = null) => api.post(`/admin/tests/${slug}/maintenance`, { enabled, message }),

  // Password Reset
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),

  // Subscription Management
  cancelSubscription: () => api.post('/subscription/cancel'),
  reactivateSubscription: () => api.post('/subscription/reactivate'),
  getSubscriptionStatus: () => api.get('/subscription/status'),
  
  // Contact
  sendContactForm: (name, email, message) => api.post('/contact', { name, email, message }),
  sendContactMessage: (data) => api.post('/contact', data),

  // Email Preferences
  unsubscribeFromEmails: (userId, token) => api.post('/email/unsubscribe', { user_id: userId, token }),
  resubscribeToEmails: () => api.post('/email/resubscribe'),
  getEmailPreferences: () => api.get('/email/preferences'),
  setEmailPreference: (subscribed) => api.post(`/email/set-preference?subscribed=${subscribed}`),

  // Token Packs
  getTokenPacks: () => api.get('/token-packs'),
  createTokenPackCheckout: (packType) => api.post('/payments/token-pack', { pack_type: packType }),

  // Admin Email Automation
  runEmailAutomation: () => api.post('/admin/run-email-automation'),
  sendAnnouncement: (subject, message, includeUnsubscribed = false) => 
    api.post('/admin/send-announcement', { subject, message, include_unsubscribed: includeUnsubscribed }),
  
  // New email notification system
  getEmailRecipientCounts: () => api.get('/admin/email/recipients'),
  previewEmail: (subject, message, recipientName = 'Customer', isHtml = false) => 
    api.post('/admin/email/preview', { subject, message, recipient_name: recipientName, is_html: isHtml }),
  sendNotificationEmail: (subject, message, recipientType, recipientEmail = null, includeUnsubscribed = false, isHtml = false) =>
    api.post('/admin/email/send', { 
      subject, 
      message, 
      recipient_type: recipientType, 
      recipient_email: recipientEmail,
      include_unsubscribed: includeUnsubscribed,
      is_html: isHtml
    }),

  // Northern style Maki test assessment
  startNorthernAssessment: (mode = 'simulation', sessionId = null) =>
    api.post('/assessments/northern/start', { mode, session_id: sessionId }),
  getNorthernAttempts: () => api.get('/assessments/northern/attempts'),
  getNorthernAttempt: (attemptId) => api.get(`/assessments/northern/attempts/${attemptId}`),
  beginNorthernActivity: (attemptId, activity) =>
    api.post(`/assessments/northern/attempts/${attemptId}/begin-activity`, { activity }),
  answerNorthernPerceptual: (attemptId, payload) =>
    api.post(`/assessments/northern/attempts/${attemptId}/answer/perceptual`, payload),
  answerNorthernBehavioural: (attemptId, payload) =>
    api.post(`/assessments/northern/attempts/${attemptId}/answer/behavioural`, payload),
  finalizeNorthernAttempt: (attemptId) =>
    api.post(`/assessments/northern/attempts/${attemptId}/finalize`),

  // Northern scenario admin
  adminListNorthernScenarios: (activity = null) =>
    api.get('/admin/northern/scenarios', { params: activity ? { activity } : {} }),
  adminCreateNorthernScenario: (data) => api.post('/admin/northern/scenarios', data),
  adminUpdateNorthernScenario: (scenarioId, data) =>
    api.put(`/admin/northern/scenarios/${scenarioId}`, data),
  adminPreviewNorthernScenario: (scenarioId) =>
    api.get(`/admin/northern/scenarios/${scenarioId}/preview`),
  adminNorthernAnalytics: () => api.get('/admin/northern/analytics'),
  
  // Special offer
  dismissSpecialOffer: () => api.post('/user/dismiss-special-offer'),
};

export default api;
